import { Schematic } from "../../../data/projects";

/*
 * The Work diagrams are drawn for the 1200×720 desktop canvas, flowing left
 * to right. On a phone held upright that flow turns to run top to bottom: the
 * diagram is transposed (desktop x → phone y, desktop y → phone x), so every
 * project — including ones added through the CMS — gets a portrait diagram
 * without a second hand-placed layout.
 */

/** Desktop node box. */
export const DESKTOP_NODE = { w: 140, h: 50 };
/** Phone node box. */
export const PHONE_NODE = { w: 100, h: 42 };

/** Where the diagram may sit in the 390×844 phone canvas (node centres). */
const X_RANGE = [68, 324] as const;
const Y_RANGE = [300, 579] as const;

export interface PortraitSchematic extends Schematic {
    boundaryD: string;
    /** Top-left of the deploy-boundary label. */
    boundaryLabel: { x: number; y: number };
    /** Edge labels that sit beside a vertical wire rather than across it. */
    sideLabels: boolean[];
}

/**
 * One phone axis as a piecewise-linear map over the desktop values it carries.
 * Starts as a plain scale, then pushes apart any two values whose nodes would
 * share a phone row/column and overlap, keeping the order.
 */
function axis(values: number[], apart: [number, number][], lo: number, hi: number, maxK: number, gap: number) {
    const ks = Array.from(new Set(values)).sort((a, b) => a - b);
    const min = ks[0];
    const max = ks[ks.length - 1];
    const k = Math.min(maxK, max > min ? (hi - lo) / (max - min) : maxK);
    const out = ks.map((v) => (v - min) * k);
    for (let i = 1; i < ks.length; i++) {
        out[i] = Math.max(out[i], out[i - 1]);
        apart.forEach(([a, b]) => {
            if (b === ks[i]) out[i] = Math.max(out[i], out[ks.indexOf(a)] + gap);
        });
    }
    const span = out[out.length - 1];
    const s = span > hi - lo ? (hi - lo) / span : 1;
    const off = lo + (hi - lo - span * s) / 2;
    const at = out.map((o) => off + o * s);
    const slope = k * s;
    return (v: number) => {
        if (v <= ks[0]) return at[0] + (v - ks[0]) * slope;
        for (let i = 1; i < ks.length; i++) {
            if (v <= ks[i]) {
                const t = (v - ks[i - 1]) / (ks[i] - ks[i - 1]);
                return at[i - 1] + (at[i] - at[i - 1]) * t;
            }
        }
        return at[at.length - 1] + (v - ks[ks.length - 1]) * slope;
    };
}

/** Pairs [lo, hi] of `key` values among nodes that share the same `other`. */
function pairs(nodes: { x: number; y: number }[], key: "x" | "y"): [number, number][] {
    const other = key === "x" ? "y" : "x";
    const out: [number, number][] = [];
    nodes.forEach((a) =>
        nodes.forEach((b) => {
            if (a !== b && Math.abs(a[other] - b[other]) < 1 && a[key] < b[key]) out.push([a[key], b[key]]);
        })
    );
    return out;
}

/** Absolute M/H/V/L/C/Q path → list of segments of absolute points. */
function parse(d: string) {
    const tok = d.match(/[MHVLCQ]|-?\d*\.?\d+/g) ?? [];
    const segs: { cmd: "M" | "L" | "C" | "Q"; pts: [number, number][] }[] = [];
    let cx = 0;
    let cy = 0;
    let i = 0;
    let cmd = "";
    const num = () => Number(tok[i++]);
    while (i < tok.length) {
        if (/[A-Z]/.test(tok[i])) cmd = tok[i++];
        if (cmd === "M" || cmd === "L") {
            cx = num();
            cy = num();
            segs.push({ cmd: cmd as "M" | "L", pts: [[cx, cy]] });
        } else if (cmd === "H") {
            cx = num();
            segs.push({ cmd: "L", pts: [[cx, cy]] });
        } else if (cmd === "V") {
            cy = num();
            segs.push({ cmd: "L", pts: [[cx, cy]] });
        } else if (cmd === "C" || cmd === "Q") {
            const n = cmd === "C" ? 3 : 2;
            const pts: [number, number][] = [];
            for (let j = 0; j < n; j++) pts.push([num(), num()]);
            [cx, cy] = pts[n - 1];
            segs.push({ cmd: cmd as "C" | "Q", pts });
        } else {
            i++;
        }
    }
    return segs;
}

export function portrait(s: Schematic): PortraitSchematic {
    const D = DESKTOP_NODE;
    const P = PHONE_NODE;
    const X = axis(s.nodes.map((n) => n.y), pairs(s.nodes, "y"), X_RANGE[0], X_RANGE[1], 0.9, P.w + 12);
    const Y = axis(s.nodes.map((n) => n.x), pairs(s.nodes, "x"), Y_RANGE[0], Y_RANGE[1], 0.6, P.h + 40);
    const T = (x: number, y: number): [number, number] => [X(y), Y(x)];

    const nodes = s.nodes.map((n) => ({ ...n, x: X(n.y), y: Y(n.x) }));

    // A wire's end that touches a node's edge on desktop touches the matching
    // (transposed) edge of the phone node.
    const snap = (x: number, y: number): [number, number] => {
        for (let i = 0; i < s.nodes.length; i++) {
            const n = s.nodes[i];
            const rx = x - n.x;
            const ry = y - n.y;
            const inside = Math.abs(rx) <= D.w / 2 + 2 && Math.abs(ry) <= D.h / 2 + 2;
            const onEdge = Math.abs(Math.abs(rx) - D.w / 2) <= 2 || Math.abs(Math.abs(ry) - D.h / 2) <= 2;
            if (inside && onEdge) {
                return [nodes[i].x + (ry / (D.h / 2)) * (P.w / 2), nodes[i].y + (rx / (D.w / 2)) * (P.h / 2)];
            }
        }
        return T(x, y);
    };

    const sideLabels: boolean[] = [];
    const edges = s.edges.map((e) => {
        const segs = parse(e.d);
        const last = segs.length - 1;
        let d = "";
        const ends: [number, number][] = [];
        let mid: [number, number] | null = null;
        let prev: [number, number] = [0, 0];
        segs.forEach((seg, si) => {
            const pts = seg.pts.map(([x, y], pi) => {
                const end = pi === seg.pts.length - 1;
                return (si === 0 || si === last) && end ? snap(x, y) : T(x, y);
            });
            d += `${seg.cmd}${pts.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ")} `;
            const endPt = pts[pts.length - 1];
            if (si === 0) ends.push(endPt);
            if (si === last) ends.push(endPt);
            // The label sits on the middle drawn segment.
            if (si === Math.max(1, Math.floor(segs.length / 2)) && si > 0) {
                if (seg.cmd === "C") {
                    const [a, b, c] = pts;
                    mid = [(prev[0] + 3 * a[0] + 3 * b[0] + c[0]) / 8, (prev[1] + 3 * a[1] + 3 * b[1] + c[1]) / 8];
                } else {
                    mid = [(prev[0] + endPt[0]) / 2, (prev[1] + endPt[1]) / 2];
                }
            }
            prev = endPt;
        });
        const [a, b] = [ends[0], ends[ends.length - 1]];
        sideLabels.push(Math.abs(b[0] - a[0]) < Math.abs(b[1] - a[1]));
        const m = mid ?? [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
        return { ...e, d: d.trim(), lx: m[0], ly: m[1] };
    });

    // Deploy boundary around the system's own (non-external) nodes, with a
    // gap in its top edge where the boundary's label sits.
    const own = nodes.filter((n) => !n.external);
    const x0 = Math.min(...own.map((n) => n.x)) - P.w / 2 - 8;
    let x1 = Math.max(...own.map((n) => n.x)) + P.w / 2 + 8;
    // Widen it to take in labels hung beside wires (≈6px per mono char), unless
    // that would swallow an external node.
    const labelled = Math.max(
        x1,
        ...edges.map((e, i) => (e.label && sideLabels[i] ? Math.min(382, e.lx + 8 + e.label.length * 6.2 + 12 + 6) : 0))
    );
    if (!nodes.some((n) => n.external && n.x - P.w / 2 < labelled + 6)) x1 = labelled;
    const y0 = Math.min(...own.map((n) => n.y)) - P.h / 2 - 22;
    const y1 = Math.max(...own.map((n) => n.y)) + P.h / 2 + 14;
    const r = 12;
    const boundaryD =
        `M${x0 + 98} ${y0} H${x1 - r} Q${x1} ${y0} ${x1} ${y0 + r} V${y1 - r} Q${x1} ${y1} ${x1 - r} ${y1} ` +
        `H${x0 + r} Q${x0} ${y1} ${x0} ${y1 - r} V${y0 + r} Q${x0} ${y0} ${x0 + r} ${y0}`;

    const annotations = s.annotations.map((a) => {
        const [cx, cy] = T(a.bx + 9, a.by + 9);
        return { ...a, bx: Math.min(366, Math.max(10, cx - 9 - 20)), by: cy - 9 };
    });

    return { nodes, tagTargets: s.tagTargets, edges, annotations, boundaryD, boundaryLabel: { x: x0 + 12, y: y0 - 12 }, sideLabels };
}
